<template>
  <div class="settings-page">
    <div class="page-head"><div><h1>服务器设置</h1><p>常用设置全部集中在这里。保存不自动重启，不会覆盖已有农场。</p></div><el-button :loading="loading" @click="load">刷新</el-button></div>
    <el-alert title="生效说明" type="info" :closable="false" show-icon description="服务器名称、加入规则和性能参数需要重新启动或创建游戏容器后生效；地图、收益率、初始小屋等仅用于创建新存档，不会更改现有农场。" />
    <el-tabs v-model="tab" class="tabs">
      <el-tab-pane label="基础设置" name="basic">
        <el-form label-position="top" class="form" :disabled="loading">
          <el-form-item label="服务器显示名称"><el-input v-model="config.serverName" maxlength="48" show-word-limit placeholder="例如：我们的星露谷" /><div class="hint">用于服务器状态及相关显示；与已创建的农场名称不同。</div></el-form-item>
          <el-form-item label="农场名称（仅新农场）"><el-input v-model="config.farmName" maxlength="40" show-word-limit /></el-form-item>
          <el-form-item label="最多可加入人数"><el-input-number v-model="config.maxPlayers" :min="1" :max="20" /></el-form-item>
          <el-form-item label="使用独立钱包"><el-switch v-model="config.separateWallets" /></el-form-item>
          <el-form-item label="允许通过 IP 加入"><el-switch v-model="config.allowIpConnections" /><span class="hint inline">仅在有网络需要时启用，Steam 邀请码仍是优先推荐方式</span></el-form-item>
          <el-form-item label="联机密码"><el-tag :type="config.hasPassword?'success':'info'">{{config.hasPassword?'已设置':'未设置'}}</el-tag>
            <el-radio-group v-model="config.passwordAction" class="password-choice">
              <el-radio value="keep">保持原样</el-radio><el-radio value="set">设置新密码</el-radio><el-radio value="clear">取消密码</el-radio>
            </el-radio-group>
            <el-input v-if="config.passwordAction==='set'" v-model="config.newPassword" type="password" show-password placeholder="至少6位，不展示原密码" maxlength="64" />
            <div class="hint">联机密码不会显示在管理端。修改后玩家需要使用新密码进入游戏。</div>
          </el-form-item>
        </el-form>
      </el-tab-pane>
      <el-tab-pane label="新农场规则" name="farm">
        <div class="notice">这些设置仅对将来创建的世界生效，不会重置或修改当前存档。</div>
        <el-form class="form" label-position="top">
          <el-form-item label="地图类型"><el-select v-model="config.farmType" style="width:100%" :options="farmMaps" /></el-form-item>
          <el-form-item label="初始小屋数量"><el-input-number v-model="config.startingCabins" :min="0" :max="8" /></el-form-item>
          <el-form-item label="作物等商品收益倍率"><el-select v-model="config.profitMargin" :options="profitOptions" style="width:100%" /></el-form-item>
          <el-form-item label="夜间怪物"><el-select v-model="config.spawnMonstersAtNight" :options="monsterOptions" style="width:100%" /></el-form-item>
          <div class="toggles">
            <div><span>混合献祭包</span><el-switch v-model="config.remixBundles" /></div>
            <div><span>混合矿井奖励</span><el-switch v-model="config.remixMines" /></div>
            <div><span>第一年完成社区中心选项</span><el-switch v-model="config.communityCenterYear1" /></div>
          </div>
        </el-form>
      </el-tab-pane>
      <el-tab-pane label="中文与语言" name="language">
        <div class="notice">游戏菜单和 NPC 对话使用每位玩家自己客户端选择的语言。服务器这里负责保留中文字体等资源，避免中文聊天字体缺失。</div>
        <el-form class="form" label-position="top">
          <el-form-item label="服务器内容语言资源"><el-select v-model="config.language" style="width:100%" :options="languages" /></el-form-item>
        </el-form>
        <p class="hint">先保存语言设置。服务器会自动检查已有资源，已安装的内容不会重复下载；首次下载前请停止游戏服务器。</p>
        <div class="action-row">
          <el-tag v-if="languageInstalled" type="success" effect="plain">所选语言资源已安装</el-tag>
          <el-button :loading="langBusy" :disabled="config.state==='running' || languageJob?.state==='running' || languageInstalled || !languageSaved" @click="downloadLanguage(false)">{{languageInstalled?'已安装，无需下载':!languageSaved?'请先保存语言设置':'下载所选语言资源'}}</el-button>
          <el-button v-if="languageInstalled" text :loading="langBusy" :disabled="config.state==='running' || !languageSaved" @click="downloadLanguage(true)">重新下载资源</el-button>
          <el-button :loading="progressBusy" @click="checkLanguage">立即刷新</el-button>
        </div>
        <div v-if="languageJob && languageJob.state!=='not-started'" class="result-panel" aria-live="polite">
          <div class="download-header">
            <div class="download-heading">
              <strong>{{labelState(languageJob)}}</strong>
              <span v-if="languageJob.state==='running'" class="live-indicator"><span class="live-dot"></span> 自动更新中</span>
            </div>
            <span v-if="downloadProgress.hasPercent || downloadSucceeded" class="progress-number">{{downloadSucceeded?'100%':downloadProgress.percent.toFixed(1)+'%'}}</span>
          </div>
          <el-progress v-if="languageJob.state==='running'||downloadSucceeded"
            :percentage="downloadSucceeded?100:downloadProgress.percent"
            :indeterminate="languageJob.state==='running'&&!downloadProgress.hasPercent"
            :stroke-width="10" :show-text="false" :status="downloadSucceeded?'success':undefined"/>
          <p class="download-tip">
            <template v-if="languageJob.state==='running'">
              <span v-if="downloadProgress.hasFiles">正在处理第 {{downloadProgress.done}} / {{downloadProgress.total}} 个下载文件。</span>
              <span v-else>正在连接资源服务器或校验文件，请查看下方详情。</span>
              页面打开期间每 3 秒自动检查进度，无需反复点击刷新。
            </template>
            <template v-else-if="downloadSucceeded">资源已下载完成，可以返回游戏服务器页面启动游戏。</template>
            <template v-else>下载未成功完成，请查看下方记录，排查问题后可以重新下载。</template>
          </p>
          <p v-if="progressError" class="download-error">{{progressError}}</p>
          <details class="log-details" :open="languageJob.state==='running'">
            <summary>下载详细记录 <span>（需要时展开查看）</span></summary>
            <pre ref="downloadLog" class="download-log">{{languageJob.log||'正在等待下载记录…'}}</pre>
          </details>
        </div>
      </el-tab-pane>
      <el-tab-pane label="高级设置" name="advanced">
        <el-form class="form" label-position="top">
          <el-form-item label="画面渲染帧率"><el-select v-model="config.renderFps" style="width:100%" :options="fpsOptions" /><div class="hint">0 表示无画面模式；VNC 查看画面时可设为 10～30 FPS，但会增加服务器资源消耗。</div></el-form-item>
          <div class="toggles">
            <div><span>详细日志</span><el-switch v-model="config.verboseLogging" /></div>
            <div><span>验证农场助手归属</span><el-switch v-model="config.enforceFarmhandOwnership" /></div>
            <div><span>允许移动玩家小屋</span><el-switch v-model="config.allowCabinRelocation" /></div>
          </div>
          <el-form-item label="小屋管理策略（查看）"><el-input :model-value="config.cabinStrategy" disabled /><div class="hint">已创建世界切换策略需要执行专用小屋迁移流程，不能直接修改 JSON，否则可能破坏玩家住房位置。</div></el-form-item>
        </el-form>
        <div class="advanced-link"><el-button plain @click="checkDiagnostics">刷新系统诊断</el-button><pre v-if="diagnostics">{{JSON.stringify(diagnostics,null,2)}}</pre></div>
      </el-tab-pane>
    </el-tabs>
    <div class="sticky-actions"><div><strong>确认修改</strong><span>保存后会提示是否需要重启，不会自动影响在线玩家。</span></div><el-button type="primary" :loading="saving" @click="save">保存全部设置</el-button></div>
  </div>
</template>
<script setup>
import { ref,reactive,computed,watch,nextTick,onMounted,onUnmounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { http } from '../plugins/axios'
import { authErrorMessage } from '../auth'
const tab=ref('basic'),loading=ref(false),saving=ref(false),langBusy=ref(false),languageJob=ref(null),diagnostics=ref(null),progressBusy=ref(false),progressError=ref(''),downloadLog=ref(null)
const config=reactive({serverName:'',farmName:'Server',maxPlayers:4,separateWallets:false,hasPassword:false,
  passwordAction:'keep',newPassword:'',allowIpConnections:false,farmType:0,profitMargin:1,startingCabins:1,
  spawnMonstersAtNight:'auto',remixBundles:false,remixMines:false,communityCenterYear1:false,
  language:'en',renderFps:0,verboseLogging:false,enforceFarmhandOwnership:true,allowCabinRelocation:true,cabinStrategy:'CabinStack',state:'exited'})
const farmMaps=[{label:'标准农场',value:0},{label:'河流农场',value:1},{label:'森林农场',value:2},{label:'山顶农场',value:3},{label:'荒野农场',value:4},{label:'四角农场',value:5},{label:'海滩农场',value:6},{label:'草原农场',value:7}]
const profitOptions=[{label:'标准收益（100%）',value:1},{label:'75%',value:0.75},{label:'50%',value:0.5},{label:'25%',value:0.25}]
const monsterOptions=[{label:'跟随地图默认设置',value:'auto'},{label:'总是生成',value:'true'},{label:'不生成',value:'false'}]
const languages=[{label:'简体中文资源',value:'zh-CN'},{label:'仅英文资源',value:'en'},{label:'日语资源',value:'ja-JP'},{label:'韩语资源',value:'ko-KR'}]
const fpsOptions=[{label:'0 · 无画面（省资源）',value:0},{label:'10 FPS',value:10},{label:'15 FPS',value:15},{label:'20 FPS',value:20},{label:'30 FPS',value:30}]
async function load(){
 loading.value=true
 try {Object.assign(config,(await http.get('/api/manage/admin/config')).data,{passwordAction:'keep',newPassword:''})}
 catch(e){ElMessage.error(authErrorMessage(e,'读取服务器配置失败'))}
 finally{loading.value=false}
}
async function save(){
 if(saving.value)return
 if(!config.farmName.trim()){ElMessage.warning('农场名称不能为空');return}
 if(config.passwordAction==='clear'){
  try{await ElMessageBox.confirm('确定取消服务器联机密码？任何获得邀请码的玩家可能进入农场。','确认安全设置',{type:'warning'})}catch(_){return}
 }
 saving.value=true
 try{
  const {data}=await http.put('/api/manage/admin/config',config)
  ElMessage.success(data.message || '设置已保存')
  await load()
  await refreshLanguage(true)
 }catch(e){ElMessage.error(authErrorMessage(e,'保存失败'))}
 finally{saving.value=false}
}
// The download lives in a separate Docker task. The browser only observes it;
// closing this page or switching tabs must never cancel the actual download.
let progressTimer=null
let isUnmounted=false
let progressRequestRunning=false
const languageSaved=computed(()=>languageJob.value?.language===config.language)
const languageInstalled=computed(()=>languageSaved.value && languageJob.value?.installed===true)
const downloadSucceeded=computed(()=>languageInstalled.value && languageJob.value?.state!=='running')
const downloadProgress=computed(()=>{
 const log=languageJob.value?.log||''
 const matches=[...log.matchAll(/Progress:\s*(\d+)\/(\d+)\s+files\s+-\s+[^\n]*?\((\d+(?:\.\d+)?)%\)/g)]
 if(!matches.length)return {hasPercent:false,percent:0,hasFiles:false,done:0,total:0}
 const latest=matches[matches.length-1]
 return {hasPercent:true,percent:Math.min(100,Math.max(0,Number(latest[3]))),
         hasFiles:true,done:Number(latest[1]),total:Number(latest[2])}
})
function clearProgressTimer(){
 if(progressTimer!==null){clearTimeout(progressTimer);progressTimer=null}
}
function scheduleProgress(){
 clearProgressTimer()
 if(!isUnmounted && document.visibilityState!=='hidden' && languageJob.value?.state==='running'){
  progressTimer=setTimeout(()=>refreshLanguage(true),3000)
 }
}
async function refreshLanguage(silent=false){
 if(progressRequestRunning||isUnmounted)return
 clearProgressTimer()
 progressRequestRunning=true
 if(!silent)progressBusy.value=true
 const oldState=languageJob.value?.state
 const oldLog=downloadLog.value
 const shouldFollow=!oldLog || oldLog.scrollHeight-oldLog.scrollTop-oldLog.clientHeight<32
 try{
  const {data}=await http.get('/api/manage/admin/language/progress',{timeout:10000})
  if(isUnmounted)return
  languageJob.value=data
  progressError.value=''
  if(oldState==='running' && data.state==='exited'){
   if(data.exitCode===0 && data.installed)ElMessage.success('语言资源下载已完成，可以启动游戏')
   else ElMessage.error('所选语言资源尚未准备好，请展开记录查看原因')
  }
  if(shouldFollow){
   await nextTick()
   if(downloadLog.value)downloadLog.value.scrollTop=downloadLog.value.scrollHeight
  }
 }catch(e){
  if(isUnmounted)return
  progressError.value='暂时无法获取最新进度，正在尝试重新连接。下载任务不会因此中断。'
  if(!silent)ElMessage.error(authErrorMessage(e,'获取下载进度失败'))
 }finally{
  progressRequestRunning=false
  progressBusy.value=false
  scheduleProgress()
 }
}
async function checkLanguage(){await refreshLanguage(false)}
async function downloadLanguage(force=false){
 if(langBusy.value||languageJob.value?.state==='running'||!languageSaved.value)return
 if(!force && languageInstalled.value){ElMessage.success('已安装所选语言资源，无需重复下载');return}
 const message=force
  ?'服务器已有所选语言资源，重新下载会产生网络流量。只有资源损坏或需要强制更新时才使用。'
  :'将下载所选语言资源，现有存档和 MOD 不会受到影响。请先停止游戏服务器。'
 try{await ElMessageBox.confirm(message,force?'确认重新下载？':'开始下载资源？',
      {confirmButtonText:force?'确认重新下载':'开始下载',cancelButtonText:'取消'})}catch(_){return}
 langBusy.value=true
 try{
  const {data}=await http.post('/api/manage/admin/language/download',null,
    {params:force?{force:true}:{}})
  ElMessage.success(data.message||'下载任务已启动')
  await refreshLanguage(false)
 }catch(e){ElMessage.error(authErrorMessage(e,'提交任务失败'))}
 finally{langBusy.value=false}
}

function onVisibilityChange(){
 if(document.visibilityState==='hidden')clearProgressTimer()
 else refreshLanguage(true)
}
watch(tab,(current)=>{if(current==='language')refreshLanguage(true)})
async function checkDiagnostics(){try{diagnostics.value=(await http.get('/api/manage/admin/diagnostics')).data}catch(e){ElMessage.error(authErrorMessage(e,'获取诊断失败'))}}
function labelState(job){
 if(job.state==='running')return '语言资源正在下载'
 if(job.state==='exited')return job.exitCode===0?'语言资源下载完成':'下载失败'
 return '尚未开始下载'
}
onMounted(()=>{
 load()
 refreshLanguage(true) // Recover a running download after navigating back or reloading.
 document.addEventListener('visibilitychange',onVisibilityChange)
})
onUnmounted(()=>{
 isUnmounted=true
 clearProgressTimer()
 document.removeEventListener('visibilitychange',onVisibilityChange)
})
</script>
<style scoped>
.settings-page{max-width:1100px}.page-head{display:flex;justify-content:space-between;align-items:center;gap:15px;margin-bottom:21px}h1{font-size:27px;margin:0}h3{font-size:17px}.page-head p,.hint,.sticky-actions span{font-size:13px;line-height:1.8;color:#6a8071}.tabs{background:#fff;border:1px solid #e6eee6;border-radius:12px;padding:20px 25px;margin-top:20px}.form{max-width:560px;margin-top:18px}.inline{margin-left:12px}.password-choice{display:flex;gap:6px;margin:12px 0}.toggles{max-width:560px;margin:16px 0}.toggles>div{display:flex;justify-content:space-between;align-items:center;gap:20px;padding:15px 0;border-bottom:1px solid #edf0ed;font-size:14px}.notice{background:#f1f7f3;border-radius:8px;padding:13px 16px;color:#536d56;font-size:13px;line-height:1.8;margin:15px 0}.action-row{display:flex;gap:10px;margin:22px 0}.sticky-actions{display:flex;justify-content:space-between;align-items:center;gap:16px;background:#fff;border:1px solid #dce8dd;border-radius:13px;padding:18px 22px;margin-top:18px}.sticky-actions strong{display:block;margin-bottom:6px}.sticky-actions span{display:block}.result-panel{background:#f4f7f0;border:1px solid #e1eadd;padding:17px;border-radius:12px;margin-top:16px}.download-header{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:12px}.download-heading{display:flex;align-items:center;flex-wrap:wrap;gap:12px;color:#3e6449}.download-heading strong{font-size:15px}.live-indicator{font-size:12px;color:#4a855c;white-space:nowrap}.live-dot{display:inline-block;width:8px;height:8px;background:#60aa71;box-shadow:0 0 0 3px #70bb7a26;border-radius:50%;margin-right:4px}.progress-number{font-weight:800;font-variant-numeric:tabular-nums;color:#3e704b;font-size:18px}.result-panel :deep(.el-progress-bar__outer){background:#deeadc}.download-tip{font-size:12px;line-height:1.9;color:#637b66;margin:12px 0 0}.download-error{font-size:12px;color:#aa6946;margin:6px 0 0}.log-details{margin-top:17px;border-top:1px solid #dce8d9;padding-top:12px}.log-details summary{font-size:13px;color:#496e51;cursor:pointer;font-weight:650}.log-details summary span{color:#899c8b;font-size:11px;font-weight:400}.download-log{overflow:auto;white-space:pre-wrap;overflow-wrap:anywhere;max-height:240px;font:12px/1.65 Consolas,monospace;margin:13px 0 0;padding:13px;border-radius:9px;background:#fffef9;color:#45614b}.advanced-link pre{overflow:auto;white-space:pre-wrap;overflow-wrap:anywhere;max-height:200px;font-size:12px}.advanced-link{margin:20px 0}@media(max-width:700px){.tabs{padding:12px}.sticky-actions{flex-wrap:wrap}.page-head{flex-wrap:wrap}}
</style>
